import fetch from 'whatwg-fetch'; // eslint-disable-line no-unused-vars
import queryString from 'query-string';
import features from 'lib/common/features';

/**
 * The Tessitura Proxy doesn't (currently) use status codes
 * to indicate errors. AFAIK this is mirroring the raw Tessitura
 * response so there's an argument that this is correct.
 * This checks for traditional status code based error anyway
 * as we could still get one if the proxy is down.
 */
function checkRawStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    let err = new Error(response.statusText);
    err.response = response;
    throw err;
  }
}

/**
 * The Tessitura Proxy doesn't (currently) use status codes
 * to indicate errors. AFAIK this is mirroring the raw Tessitura
 * response so there's an argument that this is correct.
 * Regardless it means we need to do some manual error handling here.
 *
 * Response in the form:
 * {
 *   id: '1', // JSONRPC-ism, can be ignored
 *   error: 'Some error', // null if OK
 *   result: {} // null if error
 * }
 */
function checkProxyStatus(response) {
  if (response.error === null && response.result !== null) {
    return response;
  } else {
    let err = new Error(response.error);
    err.response = response;
    throw err;
  }
}

/**
 * Handle session key injection
 * Replaces %sessionKey% placeholder with real session key
 */
function injectSessionKey(body, sessionKey) {
  Object.keys(body.params).map(function(k) {
    if (body.params[k] === '%sessionKey%') {
      body.params[k] = sessionKey;
    }
    return k;
  });
  return body;
}

/**
 * Create a Request object for POST methods
 * - Content-Type is set to text/plain to avoid pre-flight request.
 * - Body must be stringified.
 */
function createPostRequest(endpoint, sessionKey, body) {
  const reqBody = injectSessionKey(body, sessionKey);
  return new Request(endpoint, {
    method: 'POST',
    mode: 'cors',
    credentials: 'same-origin',
    headers: new Headers({
      'Content-Type': 'text/plain'
    }),
    body: JSON.stringify(reqBody)
  });
}

/**
 * Create a Request object for GET methods
 *
 * GET with JSONRPC is really awkward.
 * - Content-Type is set to text/plain to avoid pre-flight request.
 * - 'id' and 'method' keys are passed as query parameters.
 * - Request Body params need to be Base64 encoded.
 */
function createGetRequest(endpoint, sessionKey, body) {
  const reqBody = injectSessionKey(body, sessionKey);
  const params = queryString.stringify(reqBody.params);

  let url = `${endpoint}/${body.method}`;
  if (params) {
    url = `${url}?${params}`;
  }

  return new Request(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'same-origin'
  });
}

class API {
  constructor(endpoint, initialSessionKey) {
    this.endpoint = endpoint;
    this.sessionKeyProp = 'sessionkey';
    this.initialSessionKey = initialSessionKey || false;
  }
  fetchNewSessionKey() {
    let req = new Request(this.endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'text/plain'
      }),
      body: JSON.stringify({
        method: 'GetNewAnonymousSessionKey',
        params: {}
      })
    });

    return window
      .fetch(req)
      .then(checkRawStatus)
      .then(resp => resp.json())
      .then(checkProxyStatus)
      .then(data => data.result);
  }
  getSessionKey() {
    /**
         * If we have already been passed a session to use
         * in the constructor then use that, or if we have
         * one in localStorage use that otherwise go and
         * fetch a new anonymous session key and store
         * *that* value in localStorage.
         */
    if (this.initialSessionKey) {
      return Promise.resolve(this.initialSessionKey);
    }

    if (features.isEnabled('cookieGetCurrentSessionKey')) {
      const cookieValue = document.cookie
        .split('; ')
        .find(row =>
          row.startsWith(
            features.getOption('cookieGetCurrentSessionKey', 'cookie')
          )
        );

      if (cookieValue) {
        const session_key = cookieValue.split('=')[1];
        return Promise.resolve(session_key);
      }
    }

    let sessionKey;
    try {
      sessionKey = localStorage.getItem(this.sessionKeyProp);
      sessionKey = JSON.parse(sessionKey);
    } catch (e) {} // eslint-disable-line no-empty

    if (sessionKey) {
      return Promise.resolve(sessionKey);
    } else {
      return new Promise(resolve => {
        this.fetchNewSessionKey().then(newSessionKey => {
          if (!sessionKey) {
            try {
              sessionKey = localStorage.setItem(
                this.sessionKeyProp,
                JSON.stringify(newSessionKey)
              );
            } catch (e) {} // eslint-disable-line no-empty
          }
          resolve(newSessionKey);
        });
      });
    }
  }
  setInitialSessionKey(sessionKey) {
    this.initialSessionKey = sessionKey;
  }
  request(body) {
    return this.getSessionKey().then(sessionKey => {
      let req = createPostRequest(this.endpoint, sessionKey, body);
      return window
        .fetch(req)
        .then(checkRawStatus)
        .then(resp => resp.json())
        .then(checkProxyStatus);
    });
  }
  requestRaw(body) {
    const req = createPostRequest(this.endpoint, null, body);
    return window
      .fetch(req)
      .then(checkRawStatus)
      .then(resp => resp.json())
      .then(checkProxyStatus);
  }
  requestGet(body) {
    return this.getSessionKey().then(sessionKey => {
      let req = createGetRequest(this.endpoint, sessionKey, body);
      return window
        .fetch(req)
        .then(checkRawStatus)
        .then(resp => resp.json())
        .then(checkProxyStatus);
    });
  }
  requestGetRaw(body) {
    const req = createGetRequest(this.endpoint, null, body);
    return window
      .fetch(req)
      .then(checkRawStatus)
      .then(resp => resp.join())
      .then(checkProxyStatus);
  }
}

export default API;



// WEBPACK FOOTER //
// ./src/javascripts/app/lib/api/tessitura-wrapper.js